"use client"

import { createContext, useContext } from "react"
import CourseStructureService from "../services/CourseStructureService"

const CourseStructureContext = createContext()

export const CourseStructureProvider = ({ children }) => {
  // === FUNCIONES PARA GESTIÓN DE ESTRUCTURA DE CURSOS ===

  const downloadModuleResource = async (moduleId, filename, originalName) => {
    return await CourseStructureService.downloadModuleResource(moduleId, filename, originalName)
  }

  // Obtener estructura completa de un curso
  const getCourseStructure = async (courseId) => {
    return await CourseStructureService.getCourseStructure(courseId)
  }

  const toggleResourceActivo = async (resourceId, isActive) => {
    return await CourseStructureService.toggleResourceActivo(resourceId, isActive)
  }

  // Obtener curso específico
  const getCourse = async (courseId) => {
    return await CourseStructureService.getCourse(courseId)
  }

  // Crear elemento de estructura
  const createStructureItem = async (itemData) => {
    return await CourseStructureService.createStructureItem(itemData)
  }

  // Actualizar elemento de estructura
  const updateStructureItem = async (itemId, itemData) => {
    return await CourseStructureService.updateStructureItem(itemId, itemData)
  }

  // Eliminar elemento de estructura
  const deleteStructureItem = async (itemId) => {
    return await CourseStructureService.deleteStructureItem(itemId)
  }

  // Reordenar elementos de estructura
  const reorderStructureItems = async (draggedItemId, targetItemId) => {
    return await CourseStructureService.reorderStructureItems(draggedItemId, targetItemId)
  }

  // Duplicar elemento de estructura
  const duplicateStructureItem = async (itemId) => {
    return await CourseStructureService.duplicateStructureItem(itemId)
  }

  // Mover elemento a otro padre
  const moveStructureItem = async (itemId, newParentId) => {
    return await CourseStructureService.moveStructureItem(itemId, newParentId)
  }

  // Obtener estadísticas de la estructura
  const getStructureStats = async (courseId) => {
    return await CourseStructureService.getStructureStats(courseId)
  }

  // Exportar estructura
  const exportStructure = async (courseId, format = "json") => {
    return await CourseStructureService.exportStructure(courseId, format)
  }

  // Importar estructura
  const importStructure = async (courseId, structureData, replaceExisting = false) => {
    return await CourseStructureService.importStructure(courseId, structureData, replaceExisting)
  }

  // Validar estructura
  const validateStructure = async (courseId) => {
    return await CourseStructureService.validateStructure(courseId)
  }

  // Obtener plantillas de estructura
  const getStructureTemplates = async () => {
    return await CourseStructureService.getStructureTemplates()
  }

  // Aplicar plantilla de estructura
  const applyStructureTemplate = async (courseId, templateId) => {
    return await CourseStructureService.applyStructureTemplate(courseId, templateId)
  }

  // Buscar en estructura
  const searchInStructure = async (courseId, searchTerm) => {
    return await CourseStructureService.searchInStructure(courseId, searchTerm)
  }

  // Obtener historial de cambios
  const getStructureHistory = async (courseId, { page = 1, limit = 20 } = {}) => {
    return await CourseStructureService.getStructureHistory(courseId, { page, limit })
  }

  // Restaurar versión anterior
  const restoreStructureVersion = async (courseId, versionId) => {
    return await CourseStructureService.restoreStructureVersion(courseId, versionId)
  }

  // === FUNCIONES PARA GESTIÓN DE RECURSOS DE MÓDULOS ===

  // Subir recursos a un módulo
  const uploadStructureResources = async (estructuraId, files, isActive) => {
    return await CourseStructureService.uploadStructureResources(estructuraId, files, isActive)
  }

  const viewModuleResource = async (estructuraId, filename) => {
    return await CourseStructureService.viewModuleResource(estructuraId, filename)
  }

  // Obtener recursos de un módulo
  const getStructureResources = async (estructuraId) => {
    return await CourseStructureService.getStructureResources(estructuraId)
  }

  // Eliminar recurso de un módulo
  const deleteModuleResource = async (resourceId) => {
    return await CourseStructureService.deleteModuleResource(resourceId)
  }
  const createQuestion = async (data) => {
    return await CourseStructureService.createQuestion(data);
  };

  // helper: convierte "/uploads/..." a URL absoluta si hace falta
  const toAbsoluteUrl = (url) => {
    return CourseStructureService.toAbsoluteUrl(url);
  };

  const createGame = async (data) => {
    return await CourseStructureService.createGame(data);
  };

  const deleteQuestion = async (questionId) => {
    try {
      return await CourseStructureService.deleteQuestion(questionId)
    } catch (error) {
      console.error("Error eliminando pregunta:", error)
      throw error
    }
  }

  const deleteGame = async (gameId) => {
    try {
      return await CourseStructureService.deleteGame(gameId)
    } catch (error) {
      console.error("Error eliminando juego:", error)
      throw error
    }
  }


  const updateQuestion = async (questionId, questionData) =>
    await CourseStructureService.updateQuestion(questionId, questionData)


  const updateGame = async (gameId, gameData) =>
    await CourseStructureService.updateGame(gameId, gameData)



  const contextValue = {
    // Funciones básicas
    getCourseStructure,
    getCourse,
    createStructureItem,
    updateStructureItem,
    deleteStructureItem,
    reorderStructureItems,
    // Funciones avanzadas
    duplicateStructureItem,
    moveStructureItem,
    getStructureStats,
    exportStructure,
    importStructure,
    validateStructure,
    // Plantillas
    getStructureTemplates,
    applyStructureTemplate,
    // Búsqueda y historial
    searchInStructure,
    getStructureHistory,
    restoreStructureVersion,
    // Recursos de módulos
    uploadStructureResources,
    getStructureResources,
    deleteModuleResource,
    downloadModuleResource,
    toggleResourceActivo,
    viewModuleResource,

    //Funciones para preguntas
    createQuestion,
    createGame,
    deleteQuestion,
    deleteGame,
    updateQuestion,
    updateGame,
    toAbsoluteUrl,
  }

  return <CourseStructureContext.Provider value={contextValue}>{children}</CourseStructureContext.Provider>
}

export const useCourseStructure = () => {
  const context = useContext(CourseStructureContext)

  if (context === undefined) {
    throw new Error("useCourseStructure must be used within a CourseStructureProvider")
  }

  return context
}
